import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {
  IAEMScreenContent,
  IAEMCommonContent,
  IAEMUserBasedAlertResponse,
  IMapdPaymentsAEMContent,
} from './interface';
import { COMMON_COMPONENTS, PAGE_NAME } from '../AEM/constants';
import { commonAEMContentReducer, screenAEMContentReducer } from './reducer';

export interface IAEMContextValue {
  setAEMScreenContent: (
    _AEMScreenContent: IAEMScreenContent,
    _Action: string,
  ) => void;
  setAEMCommonContent: (
    _AEMCommonContent: IAEMCommonContent,
    _Action: string,
  ) => void;
  setUserBasedAlertDetails: (
    userBasedAlertDetails: IAEMUserBasedAlertResponse,
  ) => void;
  AEMScreenContent: IAEMScreenContent;
  AEMCommonContent: IAEMCommonContent;
  userBasedAlertDetails: IAEMUserBasedAlertResponse;
}

export const initialAEMScreenContent: IAEMScreenContent = {
  [PAGE_NAME.MEMBER_VALIDATION]: {
    alert: {
      noBillAvailable: '',
      loadMemberLoginError: '',
      mtNoActivePolicies: '',
    },
    label: {
      continueButton: '',
      dateOfBirth: '',
      exitLink: '',
      memberID: '',
      memberIDInformation: '',
      orLabel: '',
      policyInformation: '',
      zipCode: '',
      paymentTermAccept: '',
      browserVersionDisclaimer: '',
      serverDate: '',
      memberAccountLogin: '',
      oneTimePayment: '',
      bannerHeading: '',
      bannerMessage: '',
      titleLoggedInMemberEnteredIncorrectInformation: '',
      bodyLoggedInMemberEntersIncorrectInformation: '',
      buttonLoggedInMemberEntersIncorrectInformation: '',
      caTitleLoggedInMemberEnteredIncorrectInfo: '',
      caBodyTermedMemberAttemptsLogIn: '',
      caButtonLoggedInMemberEnteredIncorrectInformation: '',
      caTitleTermedMemberAttemptsLogIn: '',
      caBodyTermedMemberAttemptsLogIn1: '',
      caButtonTermedMemberAttemptsLogIn: '',
      titleTermedMemberAttemptsLogIn: '',
      bodyTermedMemberAttemptsLogIn: '',
      buttonTermedMemberAttemptsLogIn: '',
    },
    inlineError: {
      invalidDOBMonth: '',
      invalidDOBMonthYear: '',
      invalidDOBYear: '',
      invalidMemberID: '',
      invalidZip: '',
    },
    image: {
      card: { altText: '', imgUrl: '' },
      cardToolTip: { altText: '', imgUrl: '' },
    },
    placeholder: {
      dateOfBirthPlaceholder: '',
    },
  },
  [PAGE_NAME.PAYMENT_TERMS]: {
    label: {
      paymentTerm: '',
      paymentTermAccept: '',
      submitButton: '',
      termsAndCondition: '',
      cancel: '',
      loaderMessage: '',
      paymentCompleteConfirmation: '',
      agree: '',
    },
    alert: {
      scrollDown: '',
      addressError: '',
    },
    inlineError: {
      addressError: '',
    },
  },
  [PAGE_NAME.ALL_PAYMENT_DETAILS]: {
    label: {
      sectionHeader: '',
      allPaymentTitle: '',
      paymentDueDate: '',
      expiringPolicy: '',
      autoPayUpdateMsg: '',
    },
    alert: {
      selectOnePayment: '',
    },
    inlineError: {
      latePayment: '',
      paymentAlertWarning: '',
    },
  },
  [PAGE_NAME.ALL_PAYMENT_DETAILS_ACTIVE]: {
    label: {
      sectionHeader: '',
      allPaymentTitle: '',
      paymentDueDate: '',
      automaticPaymentsLabel: '',
      onLabel: '',
      manageLink: '',
      offLabel: '',
      nextAutopayBillPayment: '',
      newPortalOverlayHeader: '',
      newPortalOverlayBody: '',
      cancelButton: '',
      nonAutoBillPayTitle: '',
      nonAutoBillPayClose: '',
      nonAutoBillPayBody: '',
      expiredPolicy: '',
      paymentMethod: '',
    },
    alert: {
      selectOnePayment: '',
    },
    inlineError: {
      latePayment: '',
      paymentAlertWarning: '',
      scheduledPaymentIndicator: '',
    },
  },
  [PAGE_NAME.PAYMENT_COMPLETED]: {
    label: {
      cancelActionText: '',
      doneText: '',
      dialogEmailEnterText: '',
      dialogEmailInputLabel: '',
      dialogTitle: '',
      emailText: '',
      paymentCompleted: '',
      paymentCompletedWarning: '',
      paymentConfirmationNumber: '',
      paymentDate: '',
      paymentMethod: '',
      paidText: '',
      print: '',
      printTooltipText: '',
      questionsConcerns: '',
      questionsConcernsAnswer: '',
      returnToPayments: '',
      submitActionText: '',
      bannerLabel: '',
      serverDate: '',
      logout: '',
      memberIdHeading: '',
      paymentProcessingTimeMsg: '',
      sendConfirmationTextMessage: '',
      yesSendEmail: '',
      mailSentSuccess: '',
      mailSentFailure: '',
      tryAgainMail: '',
    },
    inlineError: {
      emailErrorMessage: '',
    },
  },
  [PAGE_NAME.CANCELLED_PAYMENT_CONFIRMATION]: {
    label: {
      paymentCompleted: '',
      editPaymentLabel: '',
      paymentDate: '',
      paymentAmountLabel: '',
      print: '',
      printTooltipText: '',
      anotherPaymentLabel: '',
      emailText: '',
      returnToPayments: '',
      paymentConfirmationNumber: '',
      questionsConcernsAnswer: '',
      questionsConcerns: '',
      paymentMethod: '',
      bannerLabel: '',
    },
  },
  [PAGE_NAME.SCHEDULED_PAYMENT_CONFIRMATION]: {
    label: {
      paymentCompleted: '',
      editPaymentLabel: '',
      paymentDate: '',
      paidText: '',
      print: '',
      printTooltipText: '',
      bannerLabel: '',
      emailText: '',
      returnToPayments: '',
      paymentConfirmationNumber: '',
      questionsConcernsAnswer: '',
      questionsConcerns: '',
      paymentMethod: '',
    },
  },
  [PAGE_NAME.CANCELLED_CONFIRMATION_INTERSTITIAL]: {
    label: {
      paymentCompleteConfirmation: '',
    },
  },
  [PAGE_NAME.UPDATE_PAYMENT_COMPLETED]: {
    label: {
      cancelActionText: '',
      doneText: '',
      dialogEmailEnterText: '',
      dialogEmailInputLabel: '',
      dialogTitle: '',
      emailText: '',
      paymentCompleted: '',
      paymentCompletedWarning: '',
      paymentConfirmationNumber: '',
      paymentDate: '',
      paymentMethod: '',
      paidText: '',
      print: '',
      printTooltipText: '',
      questionsConcerns: '',
      questionsConcernsAnswer: '',
      returnToPayments: '',
      submitActionText: '',
      bannerLabel: '',
      serverDate: '',
      logout: '',
      returnToPaymentHistoryButton: '',
    },
    inlineError: {
      emailErrorMessage: '',
    },
  },
  [PAGE_NAME.PAYMENT_DETAILS]: {
    label: {
      sectionHeader: '',
      currentBalance: '',
      minimumDue: '',
      latePaymentTitle: '',
      upcomingPaymentTitle: '',
      makePaymentButton: '',
      paymentDueDate: '',
      noUpcomingPaymentLabel: '',
      currentPayment: '',
      expiringPolicy: '',
      noUpcomingBills: '',
      noCurrentBalance: '',
      autoPayUpdateMsg: '',
    },
    inlineError: {
      paymentOverdue: '',
    },
    alert: {
      latePaymentAlert: '',
      paymentProcessing: '',
      governmentCurrentBalanceWarningMsg: '',
      mapdUnableToProcess: '',
      pdpUnableToProcess: '',
      govtSchedulePaymentMsg: '',
      autoBillPayDisabledHeading: '',
      autoBillPayDisabledBodyCopy: '',
    },
  },
  [PAGE_NAME.PREMIUM_PAYMENTS]: {
    label: {
      sectionHeader: '',
      currentBalance: '',
      minimumDue: '',
      latePaymentTitle: '',
      upcomingPaymentTitle: '',
      makePaymentButton: '',
      paymentDueDate: '',
      noUpcomingPaymentLabel: '',
      subtitle: '',
      intro: '',
      oneTimeOnlinePaymentText: '',
      oneTimeOnlinePaymentUrl: '',
      automaticPaymentsText: '',
      automaticPremiumPaymentsUrl: '',
      rrbBenefitsText: '',
      rrbBenefitsWithdrawalUrl: '',
      moreDetailsText: '',
      premiumPaymentsFooterLinkUrl: '',
    },
    inlineError: {
      paymentOverdue: '',
    },
    alert: {
      latePaymentAlert: '',
      paymentProcessing: '',
    },
  },
  [PAGE_NAME.HS_PAYMENT_HISTORY]: {
    label: {
      title: '',
      tableHeader: '',
      showingResults: '',
      newestToOldest: '',
      oldestToNewest: '',
      printButton: '',
    },
  },
  [PAGE_NAME.PAYMENT_DETAILS_ACTIVE_MEMBER]: {
    label: {
      sectionHeader: '',
      currentBalance: '',
      minimumDue: '',
      latePaymentTitle: '',
      upcomingPaymentTitle: '',
      makePaymentButton: '',
      paymentDueDate: '',
      noUpcomingPaymentLabel: '',
      scheduledPaymentHeader: '',
      paymentScheduledLabel: '',
      automaticPaymentsLabel: '',
      onLabel: '',
      manageLink: '',
      offLabel: '',
      newVersionButton: '',
      noUpcomingBills: '',
      nextAutopayBillPayment: '',
      newPortalOverlayHeader: '',
      newPortalOverlayBody: '',
      cancelButton: '',
      nonAutoBillPayTitle: '',
      nonAutoBillPayClose: '',
      nonAutoBillPayBody: '',
      pageHeading: '',
      amountLabel: '',
      confirmationNumber: '',
      scheduledLabel: '',
      detailsLabel: '',
      declinedLabel: '',
      accountChargedLabel: '',
      appliedToBillLabel: '',
      noRecentPaymentMessage: '',
      cancelledLabel: '',
      returnedLabel: '',
      autopayModalTitle: '',
      autopayModalDescription: '',
      autopayModalCloseButton: '',
      autopayModalContinueButton: '',
      govtAutoPayModalTitle: '',
      govtAutoPayModalDescription: '',
      govtAutoPayModalCloseButton: '',
      govtAutoPayModalContinueButton: '',
      expiredPolicy: '',
      recurringACHLabel: '',
      ezBluePaymentLabel: '',
      paymentMethod: '',
    },
    inlineError: {
      paymentOverdue: '',
    },
    alert: {
      latePaymentAlert: '',
      paymentProcessing: '',
      mapdOrPdpUnableToProcess: '',
    },
  },
  [PAGE_NAME.MAKE_PAYMENT]: {
    label: {
      sectionHeader: '',
      editPaymentTitle: '',
      cancelLink: '',
      currentBalance: '',
      minimumDue: '',
      paymentDetailsTitle: '',
      otherAmountLabel: '',
      paymentDateLabel: '',
      paymentAmountLabel: '',
      currentBalanceRadioLabel: '',
      minDueRadioLabel: '',
      reviewPaymentButtonLabel: '',
      paymentMethodLabel: '',
      billingInfoNote: '',
      sectionTitle: '',
      serverDate: '',
      invalidDataEntryForGovUser: '',
      paymentAccountBlockedForGovUser: '',
      bankAccountHolder: '',
      confirmBankAccountHolderEmail: '',
      confirmSubscriberEmail: '',
    },
    alert: {
      cardNotSupported: '',
      invalidDataEntry: '',
      paymentAccountBlocked: '',
      paymentProcessing: '',
      emailNotPresent: '',
      issueWithEmail: '',
      governmentCurrentBalanceWarningMsg: '',
      attentionCATitle: '',
    },
    inlineError: {
      minimumAmountErrorMsg: '',
      maximumAmountErrorMsg: '',
      moreThanDueAmountErrorMsg: '',
      invalidEmailError: '',
    },
  },
  [PAGE_NAME.BINDER_MAKE_PAYMENT]: {
    label: {
      sectionTitle: '',
      cancelLink: '',
      currentBalanceLabel: '',
      paymentTermAccept: '',
      paymentInformationTitle: '',
      paymentInformationDescription: '',
      paymentDateLabel: '',
      paymentAmountLabel: '',
      paymentAmountMessage: '',
      reviewPaymentButtonLabel: '',
      paymentMethodLabel: '',
      billingInfoNote: '',
      selectNewPaymentMethod: '',
      newBankAccount: '',
      newCard: '',
      savePaymentCheckBox: '',
      phoneLabel: '',
      emailLabel: '',
      billingAddressLabel: '',
      confirmBillingMessage: '',
      confirmBillingLabel: '',
      autopayHeading: '',
      autopayLabel: '',
      autopayMessage: '',
      autopayOff: '',
      autopayOn: '',
      samePaymentMethodText: '',
      nextPaymentAmountLabel: '',
      nextPaymentDateLabel: '',
      nextPaymentDateMessage: '',
      autopayEmail: '',
      autopayEmailMessage: '',
      paymentMethodSubmittedText: '',
      serverDate: '',
      nicknameLabel: '',
      paymentProcessedModalButton: '',
      paymentProcessedModalTitle: '',
      paymentProcessedModalDescription: '',
      paymentScheduledModalTitle: '',
      paymentScheduledModalDescriptionHeading: '',
      paymentScheduledModalDescriptionMessage: '',
      paymentScheduledModalCancelButton: '',
      paymentScheduledModalSubmitButton: '',
    },
    alert: {
      cardNotSupported: '',
      invalidDataEntry: '',
      paymentAccountBlocked: '',
      selectBankAccountAlert: '',
      notifyOnAmountChangeAlert: '',
      paymentMethodFail: '',
      binderPaymentAdded: '',
    },
    inlineError: {
      invalidNickname: '',
      duplicateNickname: '',
    },
  },
  [PAGE_NAME.BINDER_PAY_LATER_MAKE_PAYMENT]: {
    label: {
      sectionTitle: '',
      cancelLink: '',
      currentBalanceLabel: '',
      remainingBalance: '',
      paymentInformationTitle: '',
      paymentInformationDescription: '',
      paymentDateLabel: '',
      paymentAmountLabel: '',
      paymentAmountMessage: '',
      reviewPaymentButtonLabel: '',
      paymentMethodLabel: '',
      savePaymentCheckBox: '',
      selectNewPaymentMethod: '',
      newCard: '',
      newBankAccount: '',
      autopayLabel: '',
      autopayHeading: '',
      autopayMessage: '',
      autopayOff: '',
      autopayOn: '',
      samePaymentMethodText: '',
      nextPaymentAmountLabel: '',
      nextPaymentDateLabel: '',
      nextPaymentDateMessage: '',
      autopayEmail: '',
      autopayEmailMessage: '',
      paymentMethodSubmittedText: '',
      serverDate: '',
      editPaymentTitle: '',
      selectPaymentAmountLabel: '',
      paymentDetailsTitle: '',
      cancelEditLabel: '',
      otherAmount: '',
      nicknameLabel: '',
      emailAddressLabel: '',
    },
    alert: {
      selectBankAccountAlert: '',
      notifyOnAmountChangeAlert: '',
      paymentMethodFail: '',
      binderPaymentAdded: '',
      cardNotSupported: '',
      invalidDataEntry: '',
      paymentAccountBlocked: '',
      currentScheduledPaymentAlert: '',
      scheduledPaymentInfoAlert: '',
      attentionCaTitle: '',
    },
    inlineError: {
      minimumAmountErrorMsg: '',
      maximumAmountErrorMsg: '',
      invalidNickname: '',
      emailErrorMessage: '',
      duplicateNickname: '',
    },
  },
  [PAGE_NAME.MAKE_PAYMENT_ACTIVE_MEMBER]: {
    label: {
      sectionHeader: '',
      editPaymentTitle: '',
      cancelLink: '',
      currentBalance: '',
      minimumDue: '',
      paymentDetailsTitle: '',
      otherAmountLabel: '',
      paymentDateLabel: '',
      paymentAmountLabel: '',
      currentBalanceRadioLabel: '',
      minDueRadioLabel: '',
      reviewPaymentButtonLabel: '',
      paymentMethodLabel: '',
      billingInfoNote: '',
      selectPaymentMethodDropDown: '',
      bankAccountDropDown: '',
      cardDropDown: '',
      savePaymentCheckBox: '',
      sectionTitle: '',
      serverDate: '',
      invalidDataEntryForGovUser: '',
      paymentAccountBlockedForGovUser: '',
      govtSchedulePaymentWarningHeading: '',
    },
    alert: {
      cardNotSupported: '',
      invalidDataEntry: '',
      paymentAccountBlocked: '',
      paymentProcessing: '',
      emailNotPresent: '',
      issueWithEmail: '',
      govtMedsupImmediatePaymentMsg: '',
      govtSchedulePaymentWarningMsg: '',
      attentionCATitle: '',
    },
    inlineError: {
      minimumAmountErrorMsg: '',
      maximumAmountErrorMsg: '',
      moreThanDueAmountErrorMsg: '',
    },
  },
  [PAGE_NAME.REVIEW_PAYMENT_DETAILS]: {
    label: {
      cancelLink: '',
      continueButton: '',
      editLink: '',
      paymentAmountLabel: '',
      paymentDateLabel: '',
      paymentMethodLabel: '',
      reviewPaymentSubTitle: '',
      reviewPaymentTitle: '',
      sectionHeader: '',
      submitButton: '',
      loaderMessage: '',
      paymentCompleteConfirmation: '',
      paymentUpdatedConfirmation: '',
      serverDate: '',
      singlePaymentCheckBox: '',
      paymentAgreementCheckBox: '',
      paymentAccountVelocityForGovUser: '',
      paymentProcessingTimeMsg: '',
      govIsAccountOrRoutingNumberInvalidLabel: '',
      disclosurePaymentCheckbox: '',
      brokerPaymentCheckbox: '',
    },
    inlineError: {
      inlineCheckBoxError: '',
    },
    alert: {
      caPaymentAccountBlocked: '',
      caPaymentAccountVelocityReached: '',
      caFundingAccountCardExpired: '',
      caInvalidAccountOrRoutingNumber: '',
      caUnsupportedCard: '',
      caCommercialCreditCard: '',
      caEmailMinimumLength: '',
      caEmailMinimumLengthGov: '',
      caEmailMaximumLengthGov: '',
      caEmailMaximumLength: '',
      errorInvalidEmailFormatMobile: '',
      errorMaximumEmailLengthMobile: '',
      fundingTokenExpiredForGovUser: '',
    },
  },
  [PAGE_NAME.BROWSER_INCOMPATIBLE_ERROR]: {
    label: {
      browserSupportPrimaryText: '',
      browserSupportSecondaryText: '',
      chromeLink: '',
      fireFoxLink: '',
      safariLink: '',
      ielink: '',
      edgeLink: '',
      operaLink: '',
    },
    image: {
      browserNotSupportedImage: {
        altText: '',
        imgUrl: '',
      },
    },
  },
  [PAGE_NAME.UNABLE_TO_PROCESS_PAYMENT]: {
    label: {
      heading: '',
      unableToProcessPaymentMessage: '',
      billingAccountVelocityReached: '',
      exitButton: '',
      returnButton: '',
      logout: '',
      emailAddressFailedValidationMessage: '',
      emailAddressNotPresentHeading: '',
      emailAddressNotPresentMessage: '',
      dependentCATitle: '',
      dependentCABody: '',
      termedCATitle: '',
      termedCABody: '',
      termedCAGovtBody: '',
      titlePaymentIssue: '',
      bodyPaymentIssue: '',
      closeButtonPaymentIssue: '',
      returnButtonPaymentIssue: '',
      caTitlePaymentIssue: '',
      caBodyPaymentIssue: '',
      caCloseButtonPaymentIssue: '',
      caReturnButtonPaymentIssue: '',
    },
  },
  [PAGE_NAME.TECHNICAL_DIFFICULTY]: {
    label: {
      heading: '',
      message: '',
      exitButton: '',
      logout: '',
      genericErrorMessage: '',
      returnToPayments: '',
      sectionHeader: '',
      autoBillPayActivationMessage: '',
      autoBillPayActivationHeader: '',
    },
  },
  [PAGE_NAME.REVIEW_PAYMENT_DETAILS_ACTIVE_MEMBER]: {
    label: {
      cancelLink: '',
      continueButton: '',
      editLink: '',
      paymentAmountLabel: '',
      paymentDateLabel: '',
      paymentMethodLabel: '',
      reviewPaymentSubTitle: '',
      reviewPaymentTitle: '',
      sectionHeader: '',
      submitButton: '',
      loaderMessage: '',
      paymentCompleteConfirmation: '',
      paymentUpdatedConfirmation: '',
      serverDate: '',
      singlePaymentCheckBox: '',
      paymentAgreementCheckBox: '',
      govIsAccountOrRoutingNumberInvalidLabel: '',
    },
    inlineError: {
      inlineCheckBoxError: '',
    },
    alert: {
      caPaymentAccountBlocked: '',
      caPaymentAccountVelocityReached: '',
      caFundingAccountCardExpired: '',
      caInvalidAccountOrRoutingNumber: '',
      caUnsupportedCard: '',
      caCommercialCreditCard: '',
      caEmailMinimumLength: '',
      caEmailMinimumLengthGov: '',
      caEmailMaximumLengthGov: '',
      caEmailMaximumLength: '',
      memberEmailInvalidFormatErrorMessageIL: '',
      memberEmailInvalidFormatErrorMessageTX: '',
      memberEmailInvalidFormatErrorMessageNM: '',
      memberEmailInvalidFormatErrorMessageOK: '',
      memberEmailInvalidFormatErrorMessageMT: '',
      memberEmailLengthValidationErrorMessageIL: '',
      memberEmailLengthValidationErrorMessageTX: '',
      memberEmailLengthValidationErrorMessageNM: '',
      memberEmailLengthValidationErrorMessageOK: '',
      memberEmailLengthValidationErrorMessageMT: '',
      govMemberEmailInvalidFormatErrorMessageIL: '',
      govMemberEmailInvalidFormatErrorMessageTX: '',
      govMemberEmailInvalidFormatErrorMessageNM: '',
      govMemberEmailInvalidFormatErrorMessageOK: '',
      govMemberEmailInvalidFormatErrorMessageMT: '',
      govMemberEmailLengthValidationErrorMessageIL: '',
      govMemberEmailLengthValidationErrorMessageTX: '',
      govMemberEmailLengthValidationErrorMessageNM: '',
      govMemberEmailLengthValidationErrorMessageOK: '',
      govMemberEmailLengthValidationErrorMessageMT: '',
      errorInvalidEmailFormatMobile: '',
      errorMaximumEmailLengthMobile: '',
      fundingTokenExpiredForGovUser: '',
    },
  },
  [PAGE_NAME.MY_WALLET]: {
    alert: {
      caEmailInvalidFormatErrorMessage: '',
      caEmailLengthValidationErrorMessage: '',
      memberEmailInvalidFormatErrorMessageIL: '',
      memberEmailLengthValidationErrorMessageIL: '',
      memberEmailInvalidFormatErrorMessageTX: '',
      memberEmailLengthValidationErrorMessageTX: '',
      memberEmailInvalidFormatErrorMessageNM: '',
      memberEmailLengthValidationErrorMessageNM: '',
      memberEmailInvalidFormatErrorMessageOK: '',
      memberEmailLengthValidationErrorMessageOK: '',
      memberEmailInvalidFormatErrorMessageMT: '',
      memberEmailLengthValidationErrorMessageMT: '',
      govEmailInvalidFormatErrorMessageIL: '',
      govEmailLengthValidationErrorMessageIL: '',
      govEmailInvalidFormatErrorMessageTX: '',
      govEmailLengthValidationErrorMessageTX: '',
      govEmailInvalidFormatErrorMessageNM: '',
      govEmailLengthValidationErrorMessageNM: '',
      govEmailInvalidFormatErrorMessageOK: '',
      govEmailLengthValidationErrorMessageOK: '',
      govEmailInvalidFormatErrorMessageMT: '',
      govEmailLengthValidationErrorMessageMT: '',
      attentionCaTitle: '',
      errorInvalidEmailFormatMobile: '',
      errorMaximumEmailLengthMobile: '',
    },
    label: {
      sectionHeader: '',
      paymentMethodsTitle: '',
      addPaymentMethodTitle: '',
      defaultPaymentMethodText: '',
      bankAccountLabel: '',
      paymentCardLabel: '',
      deleteModalCancelButton: '',
      deleteModalDeleteButton: '',
      deleteModalDescription: '',
      deleteModalTitle: '',
      cannotDeleteModalDescription: '',
      cannotDeleteModalOkayButton: '',
      cannotDeleteModalTitle: '',
      automaticPaymentsLabel: '',
      onLabel: '',
      manageLink: '',
      offLabel: '',
      newVersionButton: '',
      newVersionMessage: '',
      nextAutopayBillPayment: '',
      myWalletToastMessageForFreeze: '',
      myWalletToastMessageForLockdown: '',
      myWalletToastMessageForNonAutoPay: '',
      mapdOrPdpUnableToProcess: '',
      govtCannotDeleteModalDescription: '',
      paymentMethodsForLabel: '',
      returnToPaymentLink: '',
      myWalletMapdTitle: '',
      myWalletPdpTitle: '',
      myWalletMedsupTitle: '',
    },
  },
  [PAGE_NAME.ADD_NEW_PAYMENT_METHOD_WALLET_BANK]: {
    alert: {
      invalidDataEntry: '',
      paymentAccountBlocked: '',
    },
    inlineError: {
      invalidNickname: '',
    },
    label: {
      addBankTitle: '',
      paymentNicknameLabel: '',
      nicknameOptionalMessageLabel: '',
      defaultMethodCheckboxLabel: '',
      saveChangesButtonLabel: '',
      cancelLinkLabel: '',
      cancelModalPositiveButton: '',
      cancelModalNegativeButton: '',
      cancelModalTitle: '',
      cancelModalDescription: '',
      membersEmailLabel: '',
      membersEmailMessage: '',
      emailNotFoundCommunicationMessage: '',
      emailNotAccessibleCommunicationMessage: '',
    },
  },
  [PAGE_NAME.BINDER_ALL_PAYMENTS_PAGE]: {
    alert: {
      selectOnePayment: '',
    },
    inlineError: {
      latePayment: '',
      paymentAlertWarning: '',
      scheduledPaymentIndicator: '',
    },
    label: {
      sectionHeader: '',
      allPaymentTitle: '',
      paymentDueDate: '',
      expiringPolicy: '',
      automaticPaymentsLabel: '',
      onLabel: '',
      offLabel: '',
    },
  },
  [PAGE_NAME.BINDER_PAYMENTS_PAGE]: {
    alert: {
      latePaymentAlert: '',
      paymentProcessing: '',
    },
    inlineError: {
      paymentOverdue: '',
    },
    label: {
      sectionHeader: '',
      currentBalance: '',
      minimumDue: '',
      latePaymentTitle: '',
      upcomingPaymentTitle: '',
      noUpcomingPaymentLabel: '',
      makePaymentButton: '',
      paymentDueDate: '',
      scheduledPaymentHeader: '',
      paymentScheduledLabel: '',
      automaticPaymentsLabel: '',
      onLabel: '',
      offLabel: '',
      editLink: '',
    },
  },
  [PAGE_NAME.ADD_NEW_PAYMENT_METHOD_WALLET_CARD]: {
    alert: {
      cardNotSupported: '',
      paymentAccountBlocked: '',
    },
    inlineError: {
      invalidNickname: '',
    },
    label: {
      paymentCardTitleLabel: '',
      paymentNicknameLabel: '',
      nicknameOptionalMessageLabel: '',
      defaultMethodCheckboxLabel: '',
      saveChangesButtonLabel: '',
      cancelLinkLabel: '',
      cancelModalPositiveButton: '',
      cancelModalNegativeButton: '',
      cancelModalTitle: '',
      cancelModalDescription: '',
      membersEmailLabel: '',
      membersEmailMessage: '',
      emailNotFoundCommunicationMessage: '',
      emailNotAccessibleCommunicationMessage: '',
    },
  },
  [PAGE_NAME.UPDATE_WALLET_BANK]: {
    inlineError: {
      invalidNickname: '',
    },
    label: {
      sectionHeader: '',
      bankAccountTitle: '',
      deleteButtonLabel: '',
      paymentNicknameLabel: '',
      accountTypeLabel: '',
      checkingAccount: '',
      savingsAccount: '',
      personalOrBusinessLabel: '',
      personal: '',
      business: '',
      checkboxLabel: '',
      optionalLabel: '',
      cancelModalPositiveButton: '',
      cancelModalNegativeButton: '',
      cancelModalTitle: '',
      cancelModalDescription: '',
      saveButtonText: '',
      cancelButtonText: '',
      saveModalCancelButton: '',
      saveModalDescription: '',
      saveModalSaveButton: '',
      saveModalTitle: '',
      deleteModalCancelButton: '',
      deleteModalDeleteButton: '',
      deleteModalDescription: '',
      deleteModalTitle: '',
      cannotDeleteModalDescription: '',
      cannotDeleteModalOkayButton: '',
      cannotDeleteModalTitle: '',
      govtCannotDeleteModalDescription: '',
    },
  },
  [PAGE_NAME.UPDATE_WALLET_CARD]: {
    inlineError: {
      invalidNickname: '',
      invalidExpDate: '',
      invalidPaymentZIP: '',
    },
    label: {
      sectionHeader: '',
      paymentCardTitle: '',
      deleteLabel: '',
      paymentNicknameLabel: '',
      nicknameOptionalMessageLabel: '',
      defaultMethodCheckboxLabel: '',
      expirationDateLabel: '',
      monthPlaceholder: '',
      yearPlaceholder: '',
      zipCode: '',
      saveChangesButtonLabel: '',
      cancelText: '',
      cancelModalPositiveButton: '',
      cancelModalNegativeButton: '',
      cancelModalTitle: '',
      cancelModalDescription: '',
      saveModalCancelButton: '',
      saveModalDescription: '',
      saveModalSaveButton: '',
      saveModalTitle: '',
      deleteModalCancelButton: '',
      deleteModalDeleteButton: '',
      deleteModalDescription: '',
      deleteModalTitle: '',
      serverDate: '',
      cannotDeleteModalDescription: '',
      cannotDeleteModalOkayButton: '',
      cannotDeleteModalTitle: '',
    },
  },
  [PAGE_NAME.VIEW_SCHEDULED_PAYMENTS]: {
    label: {
      pageHeading: '',
      pageSubHeading: '',
      confirmationNumber: '',
      amountLabel: '',
      paymentDate: '',
      paymentMethod: '',
      pageTitle: '',
      print: '',
      printTooltip: '',
      editButton: '',
      cancelButton: '',
      paymentCutoffMessage: '',
      qncLabel: '',
      qncMessage: '',
      cancelModalTitle: '',
      cancelModalDescription: '',
      cancelModalYesButton: '',
      cancelModalNoButton: '',
      serverDate: '',
      memberId: '',
    },
  },
  [PAGE_NAME.BINDER_VIEW_SCHEDULED_PAYMENTS]: {
    label: {
      pageHeading: '',
      pageSubHeading: '',
      confirmationNumber: '',
      amountLabel: '',
      paymentDate: '',
      paymentMethod: '',
      pageTitle: '',
      print: '',
      printTooltip: '',
      editButton: '',
      cancelButton: '',
      paymentCutoffMessage: '',
      qncLabel: '',
      qncMessage: '',
      cancelModalTitle: '',
      cancelModalDescription: '',
      cancelModalYesButton: '',
      cancelModalNoButton: '',
      serverDate: '',
    },
  },
  [PAGE_NAME.VIEW_PAYMENT_TRACKER_DETAILS_SCREEN]: {
    label: {
      pageHeading: '',
      backButton: '',
      confirmationNumber: '',
      amountLabel: '',
      paymentDate: '',
      paymentMethod: '',
      editButton: '',
      cancelButton: '',
      paymentCutoffMessage: '',
      qncLabel: '',
      qncMessage: '',
      cancelModalTitle: '',
      cancelModalDescription: '',
      cancelModalYesButton: '',
      cancelModalNoButton: '',
      scheduledLabel: '',
      accountChargedLabel: '',
      appliedToBillLabel: '',
      declinedLabel: '',
      cancelledLabel: '',
      returnedLabel: '',
      serverDate: '',
      scheduledAutopayMessage: '',
    },
  },
  [PAGE_NAME.AUTO_PAYMENT_SETTINGS]: {
    inlineError: {
      paymentMethodInlineError: '',
      inlineCheckBoxError: '',
      accountHolderEmailInlineError: '',
      invalidNickname: '',
      duplicateNickname: '',
    },
    label: {
      cancelLabel: '',
      confirmLabel: '',
      emailMessage: '',
      emailLabel: '',
      nextPaymentDateMessage: '',
      nextPaymentDateLabel: '',
      billsAppliedLabel: '',
      nextPaymentAmountLabel: '',
      firstAutoBillPayDateLabel: '',
      firstAutoBillPayDateDisclaimer: '',
      savePaymentMethodLabel: '',
      newBankAccountLabel: '',
      bankAccountMessage: '',
      autopayToggleOnLabel: '',
      autopayToggleOffLabel: '',
      paymentMethodLabel: '',
      manageAutopayMessage: '',
      autopaymentTitleLabel: '',
      autopayManageSettingLabel: '',
      selectPaymentMethodLabel: '',
      oneTimeLatePaymentAmountLabel: '',
      latePaymentMessage: '',
      noEmailFoundMessage: '',
      issueRetrievingEmailMessage: '',
      serverDate: '',
      recurringPaymentCheckBox: '',
      recurringPaymentBinderPayLaterCheckbox: '',
      bankAccountHolder: '',
      bamemailAddressHeading: '',
      bamemailMessage: '',
      accountHolderEmailMessage: '',
      abpcommunicationHeading: '',
      nonMemberBankActChk: '',
      nicknameLabel: '',
      applicantEmailLabel: '',
      newVersionButton: '',
      newVersionMessage: '',
      noUpcomingBills: '',
      changePaymentMethodLabel: '',
      freezePeriodAlertMsg: '',
      lockdownPeriodAlertMsg: '',
      termedMemberAutopayOffMessage: '',
      termedMemberAutopayOnMessage: '',
      autoBillPayAppliedPoliciesLabel: '',
      disclosureAutopayCheckbox: '',
      brokerAutopayCheckbox: '',
      titleAutoBillPayTerms: '',
      bodyAutoBillPayTerms: '',
    },
    alert: {
      autoPaymentTurnedOffAlert: '',
      autoPaymentTurnedOnAlert: '',
      automaticPaymentsNoBalance: '',
      autoPaymentsOneBalance: '',
      autoPaymentsNextMonth: '',
      autoPaymentsLatePayments: '',
      invalidDataEntry: '',
      paymentAccountBlocked: '',
      errorLoadAutoPayEmail: '',
      noAutoPayEmail: '',
      autoPayOnAlert: '',
      autoPayOffAlert: '',
      autoPayUpdateAlert: '',
      autopayActHolderEmailAlert: '',
      autoBillPayDelayed: '',
      caEmailMinimumLength: '',
      caEmailMaximumLength: '',
      memberEmailMinimumLengthIL: '',
      memberEmailMaximumLengthIL: '',
      memberEmailMinimumLengthTX: '',
      memberEmailMaximumLengthTX: '',
      memberEmailMinimumLengthNM: '',
      memberEmailMaximumLengthNM: '',
      memberEmailMinimumLengthOK: '',
      memberEmailMaximumLengthOK: '',
      memberEmailMinimumLengthMT: '',
      memberEmailMaximumLengthMT: '',
      attentionCATitle: '',
      errorInvalidEmailFormatMobile: '',
      errorMaximumEmailLengthMobile: '',
      autopayAssistanceAlert: '',
    },
  },
  [PAGE_NAME.AUTO_PAYMENT_SUCCESS]: {
    label: {
      autopaySuccessTitle: '',
      autoBillPayAppliedPoliciesLabel: '',
      returnToPayments: '',
      sectionHeader: '',
      autoBillPayActivationHeader: '',
      autoBillPayActivationMessage: '',
      autoBillPayConfirmationEmail: '',
      autoBillPaySendEmail: '',
      autopaySuccessSectionHeader: '',
      yesSendEmail: '',
      tryAgainMail: '',
      sendConfirmationTextMessage: '',
    },
    alert: {
      autopayEditAlert: '',
      brokerAutopayAssistanceAlert: '',
      mailSentSuccess: '',
      mailSentFailure: '',
    },
  },
  [PAGE_NAME.PAYMENT_HISTORY]: {
    label: {
      autoBillPayTooltip: '',
      sectionHeader: '',
      viewResultsLabel: '',
      print: '',
      printTooltipText: '',
      filter: '',
      confirmationLabel: '',
      methodLabel: '',
      processedLabel: '',
      cancelledLabel: '',
      returnedLabel: '',
      scheduledLabel: '',
      declinedLabel: '',
      refundedByCheckLabel: '',
      accountChargedLabel: '',
      appliedToBillLabel: '',
      applyFiltersButton: '',
      clearAllFiltersButton: '',
      closeButton: '',
      dateRange: '',
      paymentHistoryFilters: '',
      paymentStatus: '',
      statusCancelled: '',
      statusRefund: '',
      statusReturned: '',
      paymentStatusLabel: '',
      amountPaidLabel: '',
      paginationPreviousText: '',
      paginationNextText: '',
      paginationDropdownText: '',
      noPaymentHistoryMessage: '',
      paymentHistoryUnavailableTitle: '',
      paymentHistoryUnavailableDescription: '',
      toDate: '',
      fromDate: '',
      lengthOfTime: '',
      lastEighteenMonths: '',
      lastSixMonths: '',
      lastThreeMonths: '',
      lastTwelveMonths: '',
      dataAvailableMsg: '',
      serverDate: '',
      customDates: '',
      dateRangeLabel: '',
      paymentDate: '',
      viewAll: '',
      lastTwentyFourMonths: '',
      dataAvailableMsgForTwentyFourMonths: '',
      returnToAllPaymentsLink: '',
      paymentHistoryForLabel: '',
      returnedOrReversedStatusHelperLabel: '',
      paidLabel: '',
      refundLabel: '',
      refundVoidedLabel: '',
      reversedLabel: '',
      removedLabel: '',
      reversedStatusHelperLabel: '',
      removedStatusHelperLabel: '',
      declinedStatusHelperLabel: '',
      paymentHistoryMapdTitle: '',
      paymentHistoryPdpTitle: '',
      paymentHistoryMedsupTitle: '',
      accountChargedLabelFilter: '',
      cancelledLabelFilter: '',
      refundLabelFilter: '',
      returnedLabelFilter: '',
      scheduledLabelFilter: '',
      declinedLabelFilter: '',
      appliedToBillLabelFilter: '',
      reversedLabelFilter: '',
      refundVoidedLabelFilter: '',
      removedLabelFilter: '',
      refundedByCheckLabelFilter: '',
      paymentHistoryDisclaimerMessage: '',
      paidVmsHelperText: '',
      achLabel: '',
    },
    inlineError: {
      invalidEndDate: '',
      invalidStartDate: '',
    },
  },
  [PAGE_NAME.BILLING_HISTORY_LIST]: {
    label: {
      sectionHeader: '',
      viewResultsLabel: ':',
      billedOn: '',
      viewAsSpreadsheet: '',
      filterButton: '',
      detailsButton: '',
      noBillsMessage: '',
      pagination: '',
      nextPage: '',
      prevPage: '',
      serverDate: '',
      paginationPreviousText: '',
      paginationNextText: '',
      paginationDropdownText: '',
      viewDateLabel: '',
      closeButton: '',
      billDetailsHeading: '',
      planName: '',
      paymentDueOn: '',
      billedPremium: '',
      billPeriod: '',
      premiumCollected: '',
      pastDueAmount: '',
      priorBalance: '',
      amountBilled: '',
      currentCharges: '',
      aptcAmount: '',
      adjustments: '',
      fees: '',
      deliveryMethod: '',
      questionsConcerns: '',
      contactCustomerService: '',
      noBillHistoryMessage: '',
      billingHistoryFilters: '',
      lastEighteenMonths: '',
      lastSixMonths: '',
      lastThreeMonths: '',
      yearToDateLabel: '',
      lengthOfTime: '',
      dataAvailabilityMessage: '',
      fromDate: '',
      toDate: '',
      dateRange: '',
      coverageLabel: '',
      applyFiltersButton: '',
      clearAllFiltersButton: '',
      customDates: '',
      expiredPlanBillDetails: '',
      expiringPlanBillDetails: '',
      expiredPlanCard: '',
      expiringPlanCard: '',
      expiredPlanFilter: '',
      expiringPlanFilter: '',
      viewPdfButton: '',
      currentBalance: '',
      pastDueAmountIncluded: '',
      paymentsReceived: '',
      expiredPolicy: '',
      expiredPolicyBillDetails: '',
      viewAll: '',
      noBillDocumentWarningAlert: '',
      lastThirtySixMonths: '',
      lastTwentyFourMonths: '',
      twentyFourMonthsDataAvailabilityMessage: '',
      thirtySixMonthsDataAvailabilityMessage: '',
      returnToAllPaymentsLink: '',
      billingHistoryForLabel: '',
      paidLabel: '',
      unPaidLabel: '',
      partiallyPaidLabel: '',
      billIssuedOnLabel: '',
      startOfBillPeriodLabel: '',
      billedAmountLabel: '',
      unpaidFilterLabel: '',
      paidFilterLabel: '',
      partiallyPaidFilterLabel: '',
      billingStatusFilterLabel: '',
      billingHistoryMapdTitle: '',
      billingHistoryMedsupTitle: '',
      billingHistoryPdpTitle: '',
    },
    inlineError: {
      invalidEndDate: '',
      invalidStartDate: '',
    },
  },
  [PAGE_NAME.BILLING_HISTORY_SPREADSHEET]: {
    label: {
      sectionHeader: '',
      viewAsList: '',
      billingHistorySubHeading: '',
      dateRange: '',
      print: '',
      printTooltipText: '',
      filter: '',
      billPeriod: '',
      priorBalance: '',
      billAmount: '',
      adjustments: '',
      paymentsReceived: '',
      fee: '',
      aptc: '',
      totalAmountDue: '',
      serverDate: '',
      lengthOfTime: '',
      lastThirtySixMonths: '',
      lastEighteenMonths: '',
      lastSixMonths: '',
      lastThreeMonths: '',
      yearToDateLabel: '',
      thirtySixMonthsDataAvailabilityMessage: '',
      dataAvailabilityMessage: '',
      applyFiltersButton: '',
      clearAllFiltersButton: '',
      fromDate: '',
      toDate: '',
      closeButton: '',
      billingHistoryFilters: '',
      viewDateLabel: '',
      noBillHistoryMessage: '',
      expiredPlanTitle: '',
      expiringPlanTitle: '',
      currentCharges: '',
      pastDueAmountIncluded: '',
      billedPremium: '',
      amountBilled: '',
      pastDueAmount: '',
    },
  },
  [PAGE_NAME.REVIEW_BINDER_PAYMENT_DETAILS]: {
    label: {
      cancelLink: '',
      editLink: '',
      paymentAmountLabel: '',
      paymentDateLabel: '',
      paymentMethodLabel: '',
      reviewPaymentSubTitle: '',
      reviewPaymentTitle: '',
      sectionHeader: '',
      submitButton: '',
      serverDate: '',
      autoPayOff: '',
      autoPayOn: '',
      autoPayHeading: '',
      autoPayLabel: '',
      autoNextPaymentLabel: '',
      balancePaidMessage: '',
      singlePaymentAuthorization: '',
      autoPaymentAuthorization: '',
      confirmationNumberLabel: '',
      paymentSubmittedMessage: '',
      redirectionMessage: '',
      printText: '',
      printTooltipText: '',
      econsentCheckboxLabel: '',
    },
    inlineError: {
      checkboxError: '',
    },
    alert: {
      caPaymentAccountBlockedMessage: '',
      caFundingAccountCardExpiredMessage: '',
      caAccountOrRoutingNumberInvalidMessage: '',
      caCommercialFundingAccountMessage: '',
      caCardDetailsInvalidOrNotSupportedMessage: '',
      caEmailInvalidFormatErrorMessage: '',
      caPaymentAccountVelocityReachedMessage: '',
      caEmailLengthValidationErrorMessage: '',
      memberEmailInvalidFormatErrorMessageIL: '',
      memberEmailInvalidFormatErrorMessageTX: '',
      memberEmailInvalidFormatErrorMessageNM: '',
      memberEmailInvalidFormatErrorMessageOK: '',
      memberEmailInvalidFormatErrorMessageMT: '',
      memberEmailLengthValidationErrorMessageIL: '',
      memberEmailLengthValidationErrorMessageTX: '',
      memberEmailLengthValidationErrorMessageNM: '',
      memberEmailLengthValidationErrorMessageOK: '',
      memberEmailLengthValidationErrorMessageMT: '',
    },
  },
  [PAGE_NAME.APPLICANT_BINDER_PAYMENT_DETAILS]: {
    label: {
      sectionTitle: '',
      instructionMessageLabel: '',
      applicantIdLabel: '',
      billingIDLabel: '',
      applicantLinkLabel: '',
      billingIDLinkLabel: '',
      dobLabel: '',
      zipCodeLabel: '',
      checkboxLabel: '',
      continueButtonLabel: '',
      serverDate: '',
    },
    inlineError: {
      invalidApplicantId: '',
      invalidZip: '',
      invalidBillingID: '',
      invalidDate: '',
    },
    placeholder: {
      dateOfBirthPlaceholder: '',
    },
    alert: {
      accountNotFound: '',
    },
  },
  [PAGE_NAME.BINDER_PAY_NOW_CONFIRMATION_PAYMENT_DETAILS]: {
    label: {
      sectionTitle: '',
      almostDoneTitle: '',
      almostDoneDescription: '',
      continueButtonText: '',
      paymentCompletedTitle: '',
      paymentSubtitle: '',
      printText: '',
      printTooltipText: '',
      paidText: '',
      paymentDateLabel: '',
      paymentMethodLabel: '',
      nextPaymentDateLabel: '',
      nextPaymentDateDescription: '',
      questionsConcerns: '',
      questionsConcernsAnswer: '',
      autopayLabel: '',
      autopayHeading: '',
      autopayOff: '',
      autopayOn: '',
      autoPaymentMethodLabel: '',
      confirmationNumberLabel: '',
    },
    alert: {
      autopayFail: '',  // ✅ add missing alert block
    },
  },
  [PAGE_NAME.BINDER_PAY_LATER_CONFIRMATION_PAYMENT_DETAILS]: {
    label: {
      sectionTitle: '',
      returnButtonText: '',
      paymentCompletedTitle: '',
      paymentSubtitle: '',
      printText: '',
      printTooltipText: '',
      paidText: '',
      paymentDateLabel: '',
      paymentMethodLabel: '',
      nextPaymentDateLabel: '',
      nextPaymentDateDescription: '',
      questionsConcerns: '',
      questionsConcernsAnswer: '',
      autopayLabel: '',
      autopayHeading: '',
      autopayOff: '',
      autopayOn: '',
      autoPaymentMethodLabel: '',
      confirmationNumberLabel: '',
      titleApplicationTracker:'',
      bodyApplicationTracker:''
    },
    alert: {
      autopayFail: '',
    },
  },
  [PAGE_NAME.BINDER_PAY_LATER_SCHEDULE_CONFIRMATION_PAYMENT_DETAILS]: {
    label: {
      sectionTitle: '',
      returnButtonText: '',
      paymentScheduleTitle: '',
      paymentScheduleDescription: '',
      printText: '',
      printTooltipText: '',
      paidText: '',
      paymentDateLabel: '',
      paymentMethodLabel: '',
      nextPaymentDateLabel: '',
      nextPaymentDateDescription: '',
      questionsConcerns: '',
      questionsConcernsAnswer: '',
      autopayLabel: '',
      autopayHeading: '',
      autopayOff: '',
      autopayOn: '',
      autoPaymentMethodLabel: '',
      confirmationNumberLabel: '',
    },
    alert: {
      autopayScheduleFail: '',
    },
  },
  [PAGE_NAME.BINDER_PAY_LATER_CANCEL_CONFIRMATION_PAYMENT_DETAILS]: {
    label: {
      sectionTitle: '',
      returnButtonText: '',
      paymentCancelTitle: '',
      paymentCancelDescription: '',
      printText: '',
      printTooltipText: '',
      paidText: '',
      paymentDateLabel: '',
      paymentMethodLabel: '',
      questionsConcerns: '',
      questionsConcernsAnswer: '',
      confirmationNumberLabel: '',
    },
    alert: {
      firstPaymentAlert: '',
    },
  },
  [PAGE_NAME.BINDER_PAY_LATER_UPDATE_CONFIRMATION_PAYMENT_DETAILS]: {
    label: {
      sectionTitle: '',
      returnButtonText: '',
      paymentUpdateTitle: '',
      paymentUpdateDescription: '',
      printText: '',
      printTooltipText: '',
      paidText: '',
      paymentDateLabel: '',
      paymentMethodLabel: '',
      questionsConcerns: '',
      questionsConcernsAnswer: '',
      confirmationNumberLabel: '',
    },
  },
  [PAGE_NAME.HPS_MANAGED_ERROR]: {
    label: {
      errorMessage: '',
    },
  },
  [PAGE_NAME.PICK_YOUR_PLAN_VIEW]: {
    label: {
      title: '',
      heading: '',
      subHeading: '',
      ifmLink: '',
      medicareLink: '',
      qncLabel: '',
      qncMessage: '',
    },
  },
  [PAGE_NAME.SELECT_PLAN]: {
    label: {
      title: '',
      intro: '',
      medicareSupplementLabel: '',
      medicareAdvantageLabel: '',
      medicarePartDLabel: '',
    },
    isLoaded: false,
  },
  [PAGE_NAME.TERMS_OF_USE]: {
    label: {
      termsOfUseTitle: '',
      termsOfUseContent: '',
      accept: '',
      continueButton: '',
      termsOfUseCheckbox: '',
    },
  },
  [PAGE_NAME.NO_ACCESS]: {
    label: {
      noAccessTitle: '',
      noAccessHeading: '',
      noAccessContent: '',
    },
  },
  [PAGE_NAME.SESSION_TIMEOUT]: {
    label: {
      sessionTimeOutTitle: '',
      sessionTimeOutHeading: '',
      sessionTimeOutContent: '',
    },
  },
  [PAGE_NAME.LOGGED_OUT]: {
    label: {
      title: '',
      loggedOutMsg: '',
      closeTab: '',
    },
  },
    [PAGE_NAME.MEDICARE_LANDING]: {
      label: {
        sectionHeader: '',
        currentBalance: '',
        minimumDue: '',
        latePaymentTitle: '',
        upcomingPaymentTitle: '',
        makePaymentButton: '',
        paymentDueDate: '',
        noUpcomingPaymentLabel: '',
      },
      inlineError: {
        paymentOverdue: '',
      },
      alert: {
        latePaymentAlert: '',
        paymentProcessing: '',
      },
    },
    [PAGE_NAME.MAPD_PAYMENTS]: {
      label: {
        mapdPremiumPaymentsHeading: '',
        mapdPremiumPaymentsIntro: '',
        mapdPremiumPaymentsPayPremiumBillLabel: '',
        mapdPremiumPaymentsNextBillAmountDueDateLabel: '',
        mapdPremiumPaymentsPaymentHistoryLabel: '',
        mapdPremiumPaymentsFooterNote: '',
        mapdOneTimeOnlinePaymentLabel: '',
        mapdOneTimeOnlinePaymentUrl: '',
      },
    },
    [PAGE_NAME.HS_GLOBAL_ERROR]: {
      label: {
        noPoliciesHeading: '',
        noPoliciesDescription: '',
      },
    },
  [PAGE_NAME.AUTO_PAY_PREMIUM_PAYMENT]: {
    label: {
      pageHeading: '',
      subscriberLabel: '',
      contractNumberLabel: '',
      paymentScheduleTitle: '',
      autopayPremiumPayments: '',
      unknownValue: '',
      autoBillPay: '',
      manageButtonLabel: '',
      withdrawalFrequencyLabel: '',
      withdrawalDayLabel: '',
      currentPaidToDateLabel: '',
      lastPaymentMadeOnLabel: '',
      nextScheduledPaymentLabel: '',
      autoBillPayLabel: '',
      autoBillPayDisclaimer: '',
      enrollmentSuccessMessage: '',
    },
  },
  [PAGE_NAME.AUTOMATIC_PAYMENTS_SETUP]: {
    label: {
      addPaymentTitle: '',
      addPaymentIntro: '',
      commSettingsTitle: '',
      whichPoliciesText: '',
      emailDisclaimer: '',
      disclaimer: '',
      labelFirst: '',
      labelLast: '',
      labelAcctType: '',
      labelAcctNumber: '',
      labelRouting: '',
      labelMonthlyWithdrawal: '',
      labelWithdrawDescription: '',
      labelPayFreq: '',
      labelEmail: '',
      labelAssigneeFirst: '',
      labelAssigneeLast: '',
      valueChecking: '',
      valueSavings: '',
      valueCorporate: '',
      valueMonthly: '',
      valueQuarterly: '',
      valueSemiannual: '',
      valueAnnual: '',
      checkboxConfirm: '',
      checkboxAgree: '',
      checkboxFlexDvh: '',
      checkboxHeartStroke: '',
      ctaConfirmAbp: '',
      ctaCancelAbp: '',
    },
  },
};

export const initialAEMCommonContent: IAEMCommonContent = {
  [COMMON_COMPONENTS.HEADER]: {
    image: {
      pageLogo: {
        altText: '',
        imgUrl: '',
        linkUrl: '',
      },
    },
    label: {
      espanolText: '',
      exitText: '',
      menu: '',
      languageAssistance: '',
      myAccount: '',
      logout: '',
      exitLabel: '',
      newMemberPortal: '',
      improvedFeatures: '',
      oldVersionButton: '',
      newVersionButton: '',
      termedMember: '',
      serverDate: '',
      changeViewPlanLabel: '',
      govPlanOption: '',
      ifmPlanOption: '',
      brokerName: '',
      brokerId: '',
      memberId: '',
    },
  },
  [COMMON_COMPONENTS.CANCEL_MODAL_CONTENT]: {
    label: {
      secondaryButton: '',
      primaryButton: '',
      title: '',
      description: '',
      stayOnPageLabel: '',
    },
  },
  [COMMON_COMPONENTS.CONVERSION_MODAL]: {
    label: {
      secondaryButton: '',
      primaryButton: '',
      title: '',
      description: '',
      stayOnPageLabel: '',
    },
  },
  [COMMON_COMPONENTS.AUTOPAY_ENROLMENT_MODAL]: {
    label: {
      secondaryButton: '',
      primaryButton: '',
      title: '',
      firstDescription: '',
      returnDescription: '',
      skipButton: '',
    },
  },
  [COMMON_COMPONENTS.CANCEL]: {
    label: {
      secondaryButton: '',
      primaryButton: '',
      title: '',
      description: '',
      stayOnPageLabel: '',
    },
  },
  [COMMON_COMPONENTS.FOOTER]: {
    label: {
      footerDetails: '',
    },
  },
  [COMMON_COMPONENTS.ACTIVE_MEMBER_FOOTER]: {
    label: {
      footerDetails: '',
    },
  },
  [COMMON_COMPONENTS.ACTIVE_GOVERNMENT_FOOTER]: {
    label: {
      footerDetails: '',
    },
  },
  [COMMON_COMPONENTS.GUEST_GOVERNMENT_FOOTER]: {
    label: {
      footerDetails: '',
    },
  },

  // TODO remove values once AEM service is ready
  [COMMON_COMPONENTS.SESSION_TIMEOUT]: {
    label: {
      exitLink: '',
      heading: '',
      body: '',
      mobileSessionTimeoutDialogBody: '',
      mobileSessionTimeoutDialogHeading: '',
      mobileSessionTimeoutDialogContinueButton: '',
      continueButton: '',
      logout: '',
    },
    alert: {
      sessionTimeOutMessage: '',
      mobileExpiredSessionTimeOutDialogBody: '',
      mobileExpiredSessionTimeOutDialogHeading: '',
    },
  },

  [COMMON_COMPONENTS.NAVIGATION_BAR]: {
    label: {
      walletHeader: '',
      paymentHeader: '',
      paymentHistory: '',
      billingHistory: '',
      paymentAndBilling: '',
      continueToNewVersion: '',
      goToOldVersion: '',
    },
  },

  [COMMON_COMPONENTS.LOADER_MESSAGE]: {
    label: {
      doNotExitLabel: '',
      loadingLabel: '',
    },
  },
  [COMMON_COMPONENTS.OLD_CONVERSION_MODAL]: {
    label: {
      secondaryButton: '',
      primaryButton: '',
      title: '',
      description: '',
      stayOnPageLabel: '',
    },
  },
  [COMMON_COMPONENTS.ECONSENT_MODAL_CONTENT]: {
    label: {
      title: '',
      description: '',
      primaryButton: '',
    },
  },
  [COMMON_COMPONENTS.HOURS_OF_OPERATION]: {
    label: {
      pdpHoursOfOperation: '',
      mapdHoursOfOperation: '',
      vmsMedsuppHoursOfOperation: '',
      tmgMedsuppHoursOfOperation: '',
    },
  },
  [COMMON_COMPONENTS.AUTOPAY_SETUP_MODAL_CONTENT]: {
    label: {
      secondaryButton: '',
      primaryButton: '',
      title: '',
      description: '',
    },
  },
  [COMMON_COMPONENTS.GUEST_AUTOPAY_SETUP_MODAL_CONTENT]: {
    label: {
      secondaryButton: '',
      primaryButton: '',
      title: '',
      description: '',
    },
  },
  [COMMON_COMPONENTS.BROKER_AUTOPAY_SETUP_MODAL_CONTENT]: {
    label: {
      secondaryButton: '',
      primaryButton: '',
      title: '',
      description: '',
    },
  },
};

export const initialUserBasedAlertDetails = {
  alertMessage: '',
  isAlertMessageRequired: false,
  isLoaded: false,
};


export const AEMContext = createContext({
  /*eslint-disable */
  setAEMScreenContent: (
    __ScreenData: IAEMScreenContent,
    __action: string,
  ) => {},
  setAEMCommonContent: (
    __CommonData: IAEMCommonContent,
    __action: string,
  ) => {},
  setUserBasedAlertDetails: (
    _userBasedAlertDetails: IAEMUserBasedAlertResponse,
  ) => {},
  AEMScreenContent: initialAEMScreenContent,
  AEMCommonContent: initialAEMCommonContent,
  userBasedAlertDetails: initialUserBasedAlertDetails,
  /*eslint-enable */
});

export function WithAEMContext({
  children,
}: {
  children: JSX.Element;
}): JSX.Element {
  const [AEMScreenContent, dispatchScreenContent] = useReducer(
    screenAEMContentReducer,
    initialAEMScreenContent,
  );
  const [AEMCommonContent, dispatchCommonContent] = useReducer(
    commonAEMContentReducer,
    initialAEMCommonContent,
  );

  const [userBasedAlertDetails, setUserBasedAlertDetails] =
    useState<IAEMUserBasedAlertResponse>(initialUserBasedAlertDetails);

  const setAEMScreenContent = (
    ScreenData: IAEMScreenContent,
    pageKey: string,
  ) => {
    dispatchScreenContent({
      type: pageKey,
      result: ScreenData,
    });
  };

  const setAEMCommonContent = (
    CommonData: IAEMCommonContent,
    pageKey: string,
  ) => {
    dispatchCommonContent({
      type: pageKey,
      result: CommonData,
    });
  };

  return (
    <AEMContext.Provider
      value={useMemo(
        () => ({
          setAEMScreenContent,
          setAEMCommonContent,
          AEMCommonContent,
          AEMScreenContent,
          userBasedAlertDetails,
          setUserBasedAlertDetails,
        }),
        [AEMScreenContent, AEMCommonContent, userBasedAlertDetails],
      )}
    >
      {children}
    </AEMContext.Provider>
  );
}

export function useAEMContext(): IAEMContextValue {
  return useContext(AEMContext);
}
